import { formatDate, fullName } from '@hr/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import {
  ArrowLeftIcon,
  ExternalLinkIcon,
  FileSpreadsheetIcon,
  FileTextIcon,
  KanbanIcon,
  LockIcon,
  PencilIcon,
  PlusIcon,
  TableIcon,
  UnlockIcon,
  UsersIcon,
} from 'lucide-react';
import { useMemo } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { EmptyState } from '@/components/page';
import { SearchInput } from '@/components/search-input';
import { StageCounts } from '@/components/stage';
import { useContestStages } from '@/features/contests/use-contest-stages';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ApplicationsTable } from '@/features/applications/applications-table';
import { Kanban } from '@/features/applications/kanban';
import { QuickAddDialog } from '@/features/applications/quick-add-dialog';
import { useStageChange } from '@/features/applications/use-stage-change';
import { ContestFormSheet } from '@/features/contests/contest-form-sheet';
import { DescriptionEditor } from '@/features/contests/description-editor';
import { EvaluationTab } from '@/features/evaluation/evaluation-tab';
import { STATUS_VARIANT } from '@/features/contests/status';
import { usePreview } from '@/features/preview/use-preview';
import { ContestRemindersButton } from '@/features/reminders/contest-reminders-button';
import { CONTEST_STATUS_LABELS, uk } from '@/i18n/uk';
import { listSearchShape, tableState, tableStateToSearch } from '@/lib/search-params';
import { errorMessage, trpc } from '@/lib/trpc';
import { isHr } from '@/lib/roles';

const searchSchema = z.object({
  ...listSearchShape,
  tab: z.enum(['candidates', 'evaluation', 'description']).optional().catch(undefined),
  view: z.enum(['table', 'kanban']).optional().catch(undefined),
  stage: z.uuid().optional().catch(undefined),
  edit: z.boolean().optional().catch(undefined),
  add: z.boolean().optional().catch(undefined),
});

export const Route = createFileRoute('/_app/contests/$contestId')({
  validateSearch: searchSchema,
  // Починаємо завантаження конкурсу ще до рендеру; помилку покаже сам компонент.
  loader: ({ context, params }) => {
    void context.queryClient
      .query(trpc.contests.get.queryOptions(params.contestId))
      .catch(() => undefined);
  },
  component: ContestPage,
});

function ContestPage() {
  const openPreview = usePreview();
  const { contestId } = Route.useParams();
  const search = Route.useSearch();
  const { me } = Route.useRouteContext();
  const navigate = useNavigate({ from: Route.fullPath });
  const qc = useQueryClient();
  const canWrite = isHr(me.role);
  const setSearch = (patch: Partial<z.infer<typeof searchSchema>>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });

  const contest = useQuery(trpc.contests.get.queryOptions(contestId));
  const view = search.view ?? 'table';
  const tableQuery = tableState(search, { sort: 'stage', dir: 'asc' });
  // Таблиця — сторінка з сервера; канбан — усі учасники (потрібні всі колонки).
  const page = useQuery({
    ...trpc.applications.list.queryOptions({
      contestId,
      page: tableQuery.page,
      pageSize: tableQuery.pageSize,
      sort: tableQuery.sort,
      dir: tableQuery.dir,
      q: search.q,
      stageId: search.stage,
    }),
    enabled: view === 'table',
    placeholderData: keepPreviousData,
  });
  const all = useQuery({
    ...trpc.applications.byContest.queryOptions(contestId),
    enabled: view === 'kanban',
  });
  const stage = useStageChange();
  const stages = useContestStages(contestId);

  const setStatus = useMutation(
    trpc.contests.update.mutationOptions({
      onSuccess: (c) => toast.success(c.status === 'closed' ? uk.contests.closed : uk.common.saved),
      onError: (e) => toast.error(errorMessage(e)),
      onSettled: () => qc.invalidateQueries(trpc.contests.pathFilter()),
    }),
  );

  const filtered = useMemo(() => {
    const q = search.q?.toLowerCase().trim();
    const digits = q?.replace(/\D/g, '');
    return (all.data ?? []).filter(
      (a) =>
        (!search.stage || a.stageId === search.stage) &&
        (!q ||
          fullName(a.candidate).toLowerCase().includes(q) ||
          Boolean(digits && a.candidate.phoneE164?.includes(digits))),
    );
  }, [all.data, search.q, search.stage]);

  if (contest.isPending) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-96" />
        <Skeleton className="h-5 w-72" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (contest.isError) {
    return <EmptyState title={errorMessage(contest.error)} />;
  }
  const c = contest.data;
  const candidatesTotal = c.stageCounts.reduce((n, x) => n + x.n, 0);
  const emptyState = (
    <EmptyState
      icon={UsersIcon}
      title={uk.applications.empty}
      hint={uk.applications.emptyHint}
      action={
        canWrite && (
          <Button onClick={() => setSearch({ add: true })}>
            <PlusIcon /> {uk.applications.add}
          </Button>
        )
      }
    />
  );

  return (
    <>
      <div className="mb-4 space-y-3">
        <Link
          to="/contests"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeftIcon className="size-4" /> {uk.contests.title}
        </Link>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-xl font-semibold tracking-tight">{c.title}</h1>
              <Badge variant="outline" className={STATUS_VARIANT[c.status]}>
                {CONTEST_STATUS_LABELS[c.status]}
              </Badge>
            </div>
            <dl className="flex flex-wrap gap-x-5 gap-y-1 text-sm">
              <Meta label={uk.contests.announcedAt} value={formatDate(c.announcedAt)} />
              <Meta label={uk.contests.endsAt} value={formatDate(c.endsAt)} />
              {c.protocol2Date && (
                <Meta label={uk.contests.protocol2Date} value={formatDate(c.protocol2Date)} />
              )}
              {c.closedEarlyAt && (
                <Meta label={uk.contests.closedEarlyAt} value={formatDate(c.closedEarlyAt)} />
              )}
              {c.projectCode && <Meta label={uk.contests.project} value={c.projectCode} />}
            </dl>
            {c.externalUrl && (
              <a
                href={c.externalUrl}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
              >
                {uk.contests.openVacancy} <ExternalLinkIcon className="size-3.5" />
              </a>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => openPreview({ url: `/api/contests/${c.id}/export.xlsx` })}
            >
              <FileSpreadsheetIcon /> {uk.contests.exportExcel}
            </Button>
            <Button
              variant="outline"
              onClick={() => openPreview({ url: `/api/contests/${c.id}/description.docx` })}
            >
              <FileTextIcon /> {uk.contests.exportWord}
            </Button>
            <ContestRemindersButton contestId={c.id} canWrite={canWrite} meId={me.id} />
            {canWrite && (
              <>
                <Button variant="outline" onClick={() => setSearch({ edit: true })}>
                  <PencilIcon /> {uk.common.edit}
                </Button>
                {c.status === 'closed' ? (
                  <Button
                    variant="outline"
                    disabled={setStatus.isPending}
                    onClick={() => setStatus.mutate({ id: c.id, status: 'open' })}
                  >
                    <UnlockIcon /> {uk.contests.reopen}
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    disabled={setStatus.isPending}
                    onClick={() => setStatus.mutate({ id: c.id, status: 'closed' })}
                  >
                    <LockIcon /> {uk.contests.close}
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
        {c.stageCounts.length > 0 && <StageCounts counts={c.stageCounts} />}
      </div>

      <Tabs
        value={search.tab ?? 'candidates'}
        onValueChange={(v) =>
          setSearch({
            tab: v === 'candidates' ? undefined : (v as 'evaluation' | 'description'),
          })
        }
      >
        <TabsList>
          <TabsTrigger value="candidates">
            {uk.contests.tabCandidates}
            <span className="ml-1 text-muted-foreground">{candidatesTotal}</span>
          </TabsTrigger>
          <TabsTrigger value="evaluation">{uk.evaluation.tab}</TabsTrigger>
          <TabsTrigger value="description">{uk.contests.tabDescription}</TabsTrigger>
        </TabsList>

        <TabsContent value="candidates" className="space-y-3 pt-2">
          <div className="flex flex-wrap items-center gap-2">
            <SearchInput
              value={search.q ?? ''}
              onChange={(q) => setSearch({ q: q || undefined, page: undefined })}
              placeholder={uk.candidates.searchPlaceholder}
            />
            <select
              aria-label={uk.applications.filterStage}
              className="h-9 rounded-md border bg-background px-2 text-sm"
              value={search.stage ?? ''}
              onChange={(e) => setSearch({ stage: e.target.value || undefined, page: undefined })}
            >
              <option value="">{uk.applications.allStages}</option>
              {stages.data?.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
            <div
              className="flex rounded-md border p-0.5"
              role="group"
              aria-label={uk.contests.tabCandidates}
            >
              <Button
                size="sm"
                variant={view === 'table' ? 'secondary' : 'ghost'}
                aria-pressed={view === 'table'}
                onClick={() => setSearch({ view: undefined })}
              >
                <TableIcon /> {uk.applications.viewTable}
              </Button>
              <Button
                size="sm"
                variant={view === 'kanban' ? 'secondary' : 'ghost'}
                aria-pressed={view === 'kanban'}
                onClick={() => setSearch({ view: 'kanban' })}
              >
                <KanbanIcon /> {uk.applications.viewKanban}
              </Button>
            </div>
            {canWrite && (
              <Button className="ml-auto" onClick={() => setSearch({ add: true })}>
                <PlusIcon /> {uk.applications.add}
              </Button>
            )}
          </div>

          {view === 'kanban' ? (
            all.isPending || !stages.data ? (
              <Skeleton className="h-48 w-full" />
            ) : candidatesTotal === 0 ? (
              emptyState
            ) : (
              <Kanban
                rows={filtered}
                stages={stages.data}
                canWrite={canWrite}
                onStageChange={stage.request}
              />
            )
          ) : (
            <ApplicationsTable
              rows={page.data?.items}
              total={page.data?.total ?? 0}
              isLoading={page.isPending}
              state={tableQuery}
              onStateChange={(next) => setSearch(tableStateToSearch(next))}
              canWrite={canWrite}
              meId={me.id}
              onStageChange={stage.request}
              empty={candidatesTotal === 0 ? emptyState : <EmptyState title={uk.common.notFound} />}
            />
          )}
        </TabsContent>

        <TabsContent value="evaluation" className="pt-2">
          <EvaluationTab contestId={c.id} canWrite={canWrite} onStageChange={stage.request} />
        </TabsContent>

        <TabsContent value="description" className="pt-2">
          <DescriptionEditor contest={c} canWrite={canWrite} />
        </TabsContent>
      </Tabs>

      {stage.dialog}
      <QuickAddDialog
        contestId={c.id}
        open={Boolean(search.add)}
        onOpenChange={(o) => setSearch({ add: o || undefined })}
      />
      <ContestFormSheet
        open={Boolean(search.edit)}
        onOpenChange={(o) => setSearch({ edit: o || undefined })}
        contest={c}
      />
    </>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-1.5">
      <dt className="text-muted-foreground">{label}:</dt>
      <dd className="font-medium">{value}</dd>
    </div>
  );
}
